import { useCallback, useEffect, useRef, useState } from 'react';

import { getToken } from '../../lib/storage';

export type TestCallStatus = 'idle' | 'connecting' | 'connected' | 'ended' | 'error';

export interface TranscriptEntry {
  role: 'user' | 'assistant';
  text: string;
}

const SAMPLE_RATE = 8000;

function resolveApiBaseUrl(): string {
  return (
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    window.location.origin
  ).replace(/\/$/, '');
}

function resolveTenantId(): string {
  return (
    localStorage.getItem('agentic_tenant_id') ||
    localStorage.getItem('tenant_id') ||
    ''
  );
}

function mulawToLinear(u8: number): number {
  const u = ~u8 & 0xff;
  const sign = u & 0x80;
  const exponent = (u >> 4) & 0x07;
  const mantissa = u & 0x0f;
  let sample = ((mantissa << 3) + 0x84) << exponent;
  sample -= 0x84;
  return sign ? -sample : sample;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Drives an in-browser test call to a workflow. The browser behaves like a
 * carrier: it streams mic audio as µ-law 8kHz over the same `/ws/voice` socket
 * (mode=browser) and plays back the agent's µ-law audio, while rendering the
 * live transcript pushed by the backend. Mirrors the phone pipeline exactly.
 */
export function useBrowserVoiceTest() {
  const [status, setStatus] = useState<TestCallStatus>('idle');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workletRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const nextStartRef = useRef(0);
  const callIdRef = useRef('');

  const teardown = useCallback(() => {
    try { wsRef.current?.close(); } catch { /* noop */ }
    wsRef.current = null;
    try { workletRef.current?.disconnect(); } catch { /* noop */ }
    workletRef.current = null;
    sourceNodesRef.current.forEach((n) => { try { n.stop(); } catch { /* noop */ } });
    sourceNodesRef.current.clear();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { void ctxRef.current?.close(); } catch { /* noop */ }
    ctxRef.current = null;
  }, []);

  const flushPlayback = useCallback(() => {
    sourceNodesRef.current.forEach((n) => { try { n.stop(); } catch { /* noop */ } });
    sourceNodesRef.current.clear();
    nextStartRef.current = ctxRef.current?.currentTime ?? 0;
  }, []);

  const schedulePlayback = useCallback((mulaw: Uint8Array) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const buffer = ctx.createBuffer(1, mulaw.length, SAMPLE_RATE);
    const ch = buffer.getChannelData(0);
    for (let i = 0; i < mulaw.length; i++) ch[i] = mulawToLinear(mulaw[i]) / 32768;

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);

    const startAt = Math.max(ctx.currentTime, nextStartRef.current);
    src.start(startAt);
    nextStartRef.current = startAt + buffer.duration;

    sourceNodesRef.current.add(src);
    src.onended = () => sourceNodesRef.current.delete(src);
  }, []);

  const handleServerMessage = useCallback((raw: string) => {
    let msg: any;
    try { msg = JSON.parse(raw); } catch { return; }
    const ws = wsRef.current;

    switch (msg.event) {
      case 'media':
        if (msg.audio) schedulePlayback(base64ToBytes(msg.audio));
        break;
      case 'clear':
        flushPlayback();
        break;
      case 'mark': {
        // Echo the mark back once playback catches up to it — this is how the
        // backend knows the caller has finished hearing the current phrase.
        const ctx = ctxRef.current;
        const delayMs = ctx ? Math.max(0, (nextStartRef.current - ctx.currentTime) * 1000) : 0;
        window.setTimeout(() => {
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ event: 'mark', name: msg.name }));
          }
        }, delayMs);
        break;
      }
      case 'transcript':
        if (msg.text) {
          setTranscript((prev) => [...prev, { role: msg.role === 'user' ? 'user' : 'assistant', text: msg.text }]);
        }
        break;
      default:
        break;
    }
  }, [schedulePlayback, flushPlayback]);

  const start = useCallback(async (workflowId: string) => {
    if (status === 'connecting' || status === 'connected') return;
    setError(null);
    setTranscript([]);
    setStatus('connecting');

    try {
      const token = getToken();
      const tenantId = resolveTenantId();
      if (!token || !tenantId) throw new Error('You must be signed in to run a test call.');

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: SAMPLE_RATE });
      ctxRef.current = ctx;
      nextStartRef.current = ctx.currentTime;
      await ctx.audioWorklet.addModule('/browser-voice-worklet.js');

      const source = ctx.createMediaStreamSource(stream);
      const worklet = new AudioWorkletNode(ctx, 'mulaw-capture-processor');
      workletRef.current = worklet;
      // Keep the graph alive without feeding the mic to the speakers (echo).
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(worklet);
      worklet.connect(sink);
      sink.connect(ctx.destination);

      const wsBase = resolveApiBaseUrl().replace(/^http/i, 'ws');
      const callId = `test-${Date.now()}`;
      callIdRef.current = callId;
      const url =
        `${wsBase}/core/ws/voice/${encodeURIComponent(workflowId)}` +
        `?mode=browser&tenantId=${encodeURIComponent(tenantId)}&token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ event: 'start', callSid: callId, streamRef: callId }));
        worklet.port.onmessage = (e: MessageEvent) => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ event: 'media', audio: bytesToBase64(new Uint8Array(e.data)) }));
          }
        };
        setStatus('connected');
      };
      ws.onmessage = (e) => handleServerMessage(typeof e.data === 'string' ? e.data : String(e.data));
      ws.onerror = () => { setError('Connection error.'); setStatus('error'); };
      ws.onclose = () => { setStatus((s) => (s === 'error' ? s : 'ended')); teardown(); };
    } catch (e: any) {
      setError(e?.message || 'Failed to start the test call.');
      setStatus('error');
      teardown();
    }
  }, [status, handleServerMessage, teardown]);

  const end = useCallback(() => {
    try { wsRef.current?.send(JSON.stringify({ event: 'stop', callSid: callIdRef.current })); } catch { /* noop */ }
    setStatus('ended');
    teardown();
  }, [teardown]);

  useEffect(() => () => teardown(), [teardown]);

  return { status, transcript, error, start, end };
}
